import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		marginBottom: 12,
		gap: theme.spacing(1.5),
	},

	settingRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		flexWrap: "wrap",
	},

	settingOption: {
		minWidth: 180,
	},

	sectionTitle: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},

	privacyNote: {
		color: theme.palette.text.secondary,
		fontSize: "0.85rem",
	},
}));

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	const [aiPrompt, setAiPrompt] = useState("");
	const [aiAtendimentoPrompt, setAiAtendimentoPrompt] = useState("");
	const [aiApiKey, setAiApiKey] = useState("");
	const [aiAnthropicApiKey, setAiAnthropicApiKey] = useState("");
	const [aiModel, setAiModel] = useState("gemini-2.5-flash");
	const [aiAnthropicModel, setAiAnthropicModel] = useState("claude-sonnet-4-6");
	const [savingPrompt, setSavingPrompt] = useState(false);
	const [savingAtendimentoPrompt, setSavingAtendimentoPrompt] = useState(false);
	const [savingApiKey, setSavingApiKey] = useState(false);
	const [savingAnthropicApiKey, setSavingAnthropicApiKey] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		if (!settings.length) return;
		setAiPrompt(getSettingValue("aiPrompt") || "");
		setAiAtendimentoPrompt(getSettingValue("aiAtendimentoPrompt") || "");
		setAiApiKey(getSettingValue("aiApiKey") || "");
		setAiAnthropicApiKey(getSettingValue("aiAnthropicApiKey") || "");
		setAiModel(getSettingValue("aiModel") || "gemini-2.5-flash");
		setAiAnthropicModel(getSettingValue("aiAnthropicModel") || "claude-sonnet-4-6");
	}, [settings]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const getSettingValue = key => {
		const item = settings.find(s => s.key === key);
		return item?.value || "";
	};

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const saveSettingValue = async (key, value) => {
		await api.put(`/settings/${key}`, { value });
		toast.success(i18n.t("settings.success"));
	};

	const handleSavePrompt = async () => {
		setSavingPrompt(true);
		try {
			await saveSettingValue("aiPrompt", aiPrompt);
		} catch (err) {
			toastError(err);
		} finally {
			setSavingPrompt(false);
		}
	};

	const handleSaveApiKey = async () => {
		setSavingApiKey(true);
		try {
			await saveSettingValue("aiApiKey", aiApiKey);
		} catch (err) {
			toastError(err);
		} finally {
			setSavingApiKey(false);
		}
	};

	const handleSaveAnthropicApiKey = async () => {
		setSavingAnthropicApiKey(true);
		try {
			await saveSettingValue("aiAnthropicApiKey", aiAnthropicApiKey);
		} catch (err) {
			toastError(err);
		} finally {
			setSavingAnthropicApiKey(false);
		}
	};

	const handleSaveAtendimentoPrompt = async () => {
		setSavingAtendimentoPrompt(true);
		try {
			await saveSettingValue("aiAtendimentoPrompt", aiAtendimentoPrompt);
		} catch (err) {
			toastError(err);
		} finally {
			setSavingAtendimentoPrompt(false);
		}
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<div className={classes.settingRow}>
						<Typography variant="body1">
							{i18n.t("settings.settings.userCreation.name")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="userCreation-setting"
							name="userCreation"
							value={
								settings && settings.length > 0 && getSettingValue("userCreation")
							}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						>
							<option value="enabled">
								{i18n.t("settings.settings.userCreation.options.enabled")}
							</option>
							<option value="disabled">
								{i18n.t("settings.settings.userCreation.options.disabled")}
							</option>
						</Select>
					</div>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Typography variant="h6" className={classes.sectionTitle}>
					{i18n.t("settings.settings.aiAssistant.title")}
				</Typography>
				<Typography className={classes.privacyNote}>
					{i18n.t("settings.settings.aiAssistant.privacyNote")}
				</Typography>

				<Paper className={classes.paper}>
					<div className={classes.settingRow}>
						<Typography variant="body1">
							{i18n.t("settings.settings.aiAssistant.provider")}
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							name="aiProvider"
							value={getSettingValue("aiProvider") || "gemini"}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						>
							<option value="gemini">Gemini</option>
							<option value="anthropic">Anthropic (Claude Sonnet)</option>
						</Select>
					</div>

					<Typography variant="subtitle2">
						{i18n.t("settings.settings.aiAssistant.geminiSection")}
					</Typography>
					<TextField
						label={i18n.t("settings.settings.aiAssistant.model")}
						margin="dense"
						variant="outlined"
						fullWidth
						name="aiModel"
						value={aiModel}
						onChange={e => setAiModel(e.target.value)}
						onBlur={e => handleChangeSetting({ target: { name: "aiModel", value: e.target.value } })}
					/>

					<TextField
						label={i18n.t("settings.settings.aiAssistant.apiKey")}
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						value={aiApiKey}
						onChange={e => setAiApiKey(e.target.value)}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSaveApiKey}
						disabled={savingApiKey}
					>
						{i18n.t("settings.settings.aiAssistant.saveApiKey")}
					</Button>

					<Typography variant="subtitle2">
						{i18n.t("settings.settings.aiAssistant.anthropicSection")}
					</Typography>
					<TextField
						label={i18n.t("settings.settings.aiAssistant.anthropicModel")}
						margin="dense"
						variant="outlined"
						fullWidth
						name="aiAnthropicModel"
						value={aiAnthropicModel}
						onChange={e => setAiAnthropicModel(e.target.value)}
						onBlur={e => handleChangeSetting({ target: { name: "aiAnthropicModel", value: e.target.value } })}
					/>

					<TextField
						label={i18n.t("settings.settings.aiAssistant.anthropicApiKey")}
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						value={aiAnthropicApiKey}
						onChange={e => setAiAnthropicApiKey(e.target.value)}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSaveAnthropicApiKey}
						disabled={savingAnthropicApiKey}
					>
						{i18n.t("settings.settings.aiAssistant.saveAnthropicApiKey")}
					</Button>

					<Typography variant="subtitle2">
						{i18n.t("settings.settings.aiAssistant.postSalePromptTitle")}
					</Typography>
					<TextField
						label={i18n.t("settings.settings.aiAssistant.prompt")}
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						minRows={8}
						value={aiPrompt}
						onChange={e => setAiPrompt(e.target.value)}
						helperText={i18n.t("settings.settings.aiAssistant.promptHelp")}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSavePrompt}
						disabled={savingPrompt}
					>
						{i18n.t("settings.settings.aiAssistant.savePrompt")}
					</Button>

					<Typography variant="subtitle2">
						{i18n.t("settings.settings.aiAssistant.atendimentoPromptTitle")}
					</Typography>
					<Typography className={classes.privacyNote}>
						{i18n.t("settings.settings.aiAssistant.atendimentoPromptHelp")}
					</Typography>
					<TextField
						label={i18n.t("settings.settings.aiAssistant.atendimentoPrompt")}
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						minRows={8}
						value={aiAtendimentoPrompt}
						onChange={e => setAiAtendimentoPrompt(e.target.value)}
					/>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSaveAtendimentoPrompt}
						disabled={savingAtendimentoPrompt}
					>
						{i18n.t("settings.settings.aiAssistant.saveAtendimentoPrompt")}
					</Button>

					<div className={classes.settingRow}>
						<div>
							<Typography variant="body1">
								{i18n.t("settings.settings.aiAssistant.autoAttendNewContactsOnly")}
							</Typography>
							<Typography className={classes.privacyNote}>
								{i18n.t("settings.settings.aiAssistant.autoAttendNewContactsOnlyHelp")}
							</Typography>
						</div>
						<Select
							margin="dense"
							variant="outlined"
							native
							name="aiAutoAttendNewContactsOnly"
							value={getSettingValue("aiAutoAttendNewContactsOnly") || "enabled"}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						>
							<option value="enabled">
								{i18n.t("settings.settings.aiAssistant.autoAttendNewContactsOnlyOptions.enabled")}
							</option>
							<option value="disabled">
								{i18n.t("settings.settings.aiAssistant.autoAttendNewContactsOnlyOptions.disabled")}
							</option>
						</Select>
					</div>

					<div className={classes.settingRow}>
						<TextField
							label={i18n.t("settings.settings.aiAssistant.maxReplies")}
							margin="dense"
							variant="outlined"
							type="number"
							name="aiMaxRepliesPerHour"
							value={getSettingValue("aiMaxRepliesPerHour") || "20"}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						/>
						<TextField
							label={i18n.t("settings.settings.aiAssistant.historyMessages")}
							margin="dense"
							variant="outlined"
							type="number"
							name="aiHistoryMessages"
							value={getSettingValue("aiHistoryMessages") || "40"}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						/>
						<TextField
							label={i18n.t("settings.settings.aiAssistant.replyDelay")}
							margin="dense"
							variant="outlined"
							type="number"
							name="aiReplyDelaySeconds"
							value={getSettingValue("aiReplyDelaySeconds") || "0"}
							className={classes.settingOption}
							inputProps={{ min: 0, max: 300 }}
							onChange={handleChangeSetting}
							helperText={i18n.t("settings.settings.aiAssistant.replyDelayHelp")}
						/>
					</div>
				</Paper>
			</Container>
		</div>
	);
};

export default Settings;
